import { useLayoutEffect } from '@react-aria/utils';
import type { PropsWithChildren } from 'react';
import { createContext, useState } from 'react';

import { typedEntries } from '@infinex-private/ts-utils/object';

import type { Breakpoint } from './breakpoints';
import { breakpoints } from './breakpoints';

/** @private Exported for internal Taurus use only. */
export const BreakpointContext = createContext<Breakpoint[]>(['base']);

/**
 * Breakpoint hooks provide a way to access the current breakpoint in
 * client-side JavaScript, they are not available in server-side environments.
 */
export function BreakpointProvider(props: PropsWithChildren) {
  const matchedBreakpoints = useMatchedBreakpoints();
  return (
    <BreakpointContext.Provider value={matchedBreakpoints}>
      {props.children}
    </BreakpointContext.Provider>
  );
}

// Utils
// ----------------------------------------------------------------------------

// avoid unnecessary query for 'base', it will always match
const { base: _unused, ...breakpointsWithoutBase } = breakpoints;

// entries from largest to smallest
const breakpointEntries = typedEntries(breakpointsWithoutBase).sort(
  ([, valueA], [, valueB]) => valueB - valueA
);

const breakpointQueries = breakpointEntries.map(
  ([, value]) => `(min-width: ${value}px)`
);

function useMatchedBreakpoints(): Breakpoint[] {
  const supportsMatchMedia =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const getMatchedBreakpoints = () => {
    const matched: Breakpoint[] = [];
    for (const i in breakpointQueries) {
      const query = breakpointQueries[i]!;
      const entry = breakpointEntries[i]!;
      if (window.matchMedia(query).matches) {
        matched.push(entry[0]!);
      }
    }
    matched.push('base');
    return matched;
  };
  // Always initialize with 'base' to match server-side rendering and avoid
  // hydration mismatches. The useLayoutEffect below will update to the actual
  // matched breakpoints synchronously before the browser paints.
  const [breakpoint, setBreakpoint] = useState<Breakpoint[]>(['base']);

  useLayoutEffect(() => {
    if (!supportsMatchMedia) {
      return;
    }

    const onResize = () => {
      const matched = getMatchedBreakpoints();
      setBreakpoint((prev) => {
        if (
          // inexpensive length check first, avoid unnecessary array iteration
          prev.length !== matched.length ||
          prev.some((breakpoint, idx) => breakpoint !== matched[idx])
        ) {
          return matched;
        }

        return prev;
      });
    };
    onResize();

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [supportsMatchMedia]);

  return breakpoint;
}
